import React from 'react';
import { NavigationProp, RouteProp, useNavigation } from '@react-navigation/native';
import { useForm } from 'react-hook-form';
import FilterModal from '../../../components/ModernFilters/FilterModal';
import CheckboxList from '../../../components/ModernFilters/CheckboxList';
import DownloadTagsList from '../../../components/ModernFilters/cameras/downloads/DownloadTagsList';
import DownloadActingBodyList from '../../../components/ModernFilters/cameras/downloads/DownloadActingBodyList';
import DownloadActingBodyUnitsList from '../../../components/ModernFilters/cameras/downloads/DownloadActingBodyUnitsList';
import DownloadUserList from '../../../components/ModernFilters/cameras/downloads/DownloadUserList';
import DownloadCameraList from '../../../components/ModernFilters/cameras/downloads/DownloadCameraList';
import { translate } from '../../../services/translate';
import { CamerasParamList } from '../../../typings/Params';
import { useAuth } from '../../../context/Auth';

interface Props {
    filters: CamerasParamList['Downloads'];
    setFilters: React.Dispatch<React.SetStateAction<CamerasParamList['Downloads']>>;
    textFilter: string;
    setTextFilter: React.Dispatch<React.SetStateAction<string>>;
}

type FiltersForm = {
    actingBodyId: number;
    actingBodyUnitIds: number[];
    tagsIds: number[];
    userId: number;
    cameraId: string | null;
};

function Filter({ filters, setFilters, textFilter }: Props) {
    const { user: authenticatedUser } = useAuth();
    const navigation = useNavigation<NavigationProp<RouteProp<CamerasParamList, 'Downloads'>>>();
    const form = useForm<FiltersForm>({
        mode: 'onChange',
        defaultValues: {
            actingBodyId: filters.actingBodyId ?? 0,
            actingBodyUnitIds: filters.actingBodyUnitIds ?? [],
            tagsIds: filters.tagsIds ?? [],
            userId: filters.userId ?? 0,
            cameraId: filters.cameraId ?? null,
        },
    });

    const canSelectActingBody = Boolean(authenticatedUser?.isAdmin || authenticatedUser?.permissions.camera_watch_download_all_acting_bodies);
    const canSelectActingBodyUnits = Boolean(authenticatedUser?.isAdmin
        || authenticatedUser?.permissions.camera_watch_download_acting_body_units
        || authenticatedUser?.permissions.camera_watch_download_all_acting_bodies);
    const actingBodyId = form.watch('actingBodyId');
    const actingBodyUnitIds = form.watch('actingBodyUnitIds');
    const userSearchActingBodyId = actingBodyId ?? 0;
    const unitListActingBodyId = canSelectActingBody
        ? userSearchActingBodyId
        : authenticatedUser?.actingBodyUnit.actingBodyId ?? 0;

    const handleFilter = (values: FiltersForm) => {
        const params = {
            textFilter: textFilter !== '' ? textFilter : undefined,
            tagsIds: values.tagsIds.length > 0 ? values.tagsIds : undefined,
            userId: values.userId !== 0 ? values.userId : undefined,
            cameraId: values.cameraId || undefined,
            actingBodyId: values.actingBodyId !== 0 ? values.actingBodyId : undefined,
            actingBodyUnitIds: values.actingBodyUnitIds.length > 0 ? values.actingBodyUnitIds : undefined,
        };

        navigation.setParams({ ...filters, page: 1, ...params });
        setFilters({ ...filters, page: 0, ...params });
    };

    const organizationalFilters = [
        ...(canSelectActingBody ? [{
            componentHandlesCount: true as const,
            name: 'actingBodyId' as const,
            label: translate('actingBody'),
            component: DownloadActingBodyList,
            cleans: ['actingBodyUnitIds' as const],
        }] : []),
        ...(canSelectActingBodyUnits ? [{
            componentHandlesCount: false as const,
            name: 'actingBodyUnitIds' as const,
            label: translate('actingBodyUnit'),
            getCount: CheckboxList.getCount,
            component: ({ name }: { name: 'actingBodyUnitIds'; }) => <DownloadActingBodyUnitsList
                name={name}
                actingBodyId={unitListActingBodyId}
            />,
        }] : []),
    ];

    return <FilterModal
        form={form}
        onFilters={handleFilter}
        cleanValues={{
            actingBodyId: 0,
            actingBodyUnitIds: [],
            tagsIds: [],
            userId: 0,
            cameraId: null,
        }}
        filters={[
            ...(organizationalFilters.length > 0 ? [{
                label: translate('organizational'),
                filters: organizationalFilters,
            }] : []),
            {
                label: translate('downloadInfoLabel'),
                filters: [{
                    componentHandlesCount: true,
                    name: 'tagsIds',
                    label: translate('tags'),
                    component: DownloadTagsList,
                }, {
                    componentHandlesCount: false,
                    name: 'userId',
                    label: translate('requester'),
                    getCount: DownloadUserList.getCount,
                    component: ({ name }) => <DownloadUserList
                        name={name}
                        actingBodyId={userSearchActingBodyId}
                        actingBodyUnitIds={actingBodyUnitIds}
                    />,
                }, {
                    componentHandlesCount: false,
                    name: 'cameraId',
                    label: translate('camera'),
                    getCount: DownloadCameraList.getCount,
                    component: DownloadCameraList,
                }],
            },
        ]}
    />;
}

export default Filter;
